/**
 * Kurzformular für die Aktions-Landing.
 *
 * Bewusst reduziert: Name, PLZ, Telefon, optional Nachricht,
 * Datenschutz-Zustimmung. Massenmarkt-Traffic (TikTok/Meta) darf
 * an einem langen Formular nicht abspringen.
 *
 * Alle sichtbaren Texte (Labels, Placeholders, Datenschutz, Erfolgs-
 * meldung, Fehler-Meldung, Button-Text) sind über den Admin unter
 * `aktion_page.contact_form` editierbar (`AdminAktionsSeite.tsx`).
 *
 * Backend-Endpoint: derselbe `/api/contact` wie das Editorial-Formular,
 * aber mit `source="aktion"`, damit Admin-Anfragen nach Quelle
 * filtern können.
 */
import { useState, type CSSProperties } from "react";
import { currentVtCode, trackConversion, trackAktion } from "@/lib/analytics";
import { Check } from "lucide-react";
import type { AktionContactForm as ContactFormConfig, SpecialOfferStyle } from "@/lib/useAktionsSeite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const PLZ_RE = /^\d{5}$/;

type Status = "idle" | "sending" | "success" | "error";

/** Typografie-Mapping (mirror aus AktionHome). In Special-Offer-Renderer identisch. */
const SIZE_PX: Record<SpecialOfferStyle["size"], string> = {
    sm: "1rem", md: "1.25rem", lg: "1.5rem", xl: "2.25rem", "2xl": "3rem", "3xl": "4.5rem",
};
const FAMILY: Record<SpecialOfferStyle["font_family"], string> = {
    serif: "var(--hf-font-display, 'Cormorant Garamond', Georgia, serif)",
    display: "var(--hf-font-display, 'Cormorant Garamond', Georgia, serif)",
    sans: "var(--hf-font-body, Inter, system-ui, sans-serif)",
    anton: "'Anton', 'Impact', 'Arial Narrow', sans-serif",
};
const WEIGHT: Record<SpecialOfferStyle["weight"], number> = { normal: 400, bold: 700, black: 900 };
function styleToCss(s: SpecialOfferStyle): CSSProperties {
    return {
        fontSize: SIZE_PX[s.size] || SIZE_PX.md,
        color: s.color || "#0F1830",
        fontFamily: FAMILY[s.font_family] || FAMILY.sans,
        fontWeight: WEIGHT[s.weight] || 400,
        textTransform: s.uppercase ? "uppercase" : "none",
        lineHeight: 1.2,
    };
}

/** Zerteilt den Privacy-Text am `{link}`-Platzhalter und rendert den Link dazwischen. */
function PrivacyText({ text, linkText, linkUrl }: { text: string; linkText: string; linkUrl: string }) {
    const parts = (text || "").split("{link}");
    if (parts.length < 2) {
        return <>{text}</>;
    }
    return (
        <>
            {parts[0]}
            <a href={linkUrl || "/datenschutz"} target="_blank" rel="noreferrer" className="underline">
                {linkText || "Datenschutzerklärung"}
            </a>
            {parts.slice(1).join("{link}")}
        </>
    );
}

type Props = {
    /** Legacy-Prop; wird ignoriert wenn `cfg` gesetzt ist (Backward-Compat). */
    ctaLabel?: string;
    testId?: string;
    cfg: ContactFormConfig;
};

export default function AktionContactForm({
    cfg,
    testId = "aktion-contact",
}: Props) {
    const [status, setStatus] = useState<Status>("idle");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [datenschutz, setDatenschutz] = useState(false);
    const [errs, setErrs] = useState<{ name?: string; plz?: string; phone?: string; datenschutz?: string }>({});

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMsg("");
        const form = e.currentTarget;
        const name = (form.elements.namedItem("name") as HTMLInputElement).value.trim();
        const plz = (form.elements.namedItem("plz") as HTMLInputElement).value.trim();
        const phone = (form.elements.namedItem("phone") as HTMLInputElement).value.trim();
        const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value.trim();
        const nextErrs: typeof errs = {};
        if (name.length < 2) nextErrs.name = "Bitte geben Sie Ihren Namen an.";
        if (!PLZ_RE.test(plz)) nextErrs.plz = "Bitte eine 5-stellige PLZ.";
        if (phone.length < 5) nextErrs.phone = "Bitte geben Sie eine Telefonnummer an.";
        if (!datenschutz) nextErrs.datenschutz = "Bitte bestätigen Sie den Datenschutzhinweis.";
        if (Object.keys(nextErrs).length > 0) {
            setErrs(nextErrs);
            return;
        }
        setErrs({});
        setStatus("sending");

        const fd = new FormData();
        fd.append("name", name);
        fd.append("email", `${plz}-aktion@no-email.hypefly.de`);
        fd.append("phone", phone);
        fd.append("location", plz);
        fd.append("message", message || `Kurzanfrage von der Aktions-Landing (PLZ ${plz}). Bitte per Telefon zurückrufen.`);
        fd.append("datenschutz", "true");
        fd.append("source", "aktion");
        fd.append("honeypot", (form.elements.namedItem("website") as HTMLInputElement).value);
        const vt = currentVtCode();
        if (vt) fd.append("vt_code", vt);

        try {
            const res = await fetch(`${BACKEND_URL}/api/contact`, { method: "POST", body: fd });
            let data: unknown = null;
            try { data = await res.json(); } catch { /* ignore */ }
            if (!res.ok) {
                setStatus("error");
                const detail = (data as { detail?: string })?.detail;
                setErrorMsg(typeof detail === "string" ? detail : (cfg.error_message || `Fehler (HTTP ${res.status}).`));
                return;
            }
            setStatus("success");
            try {
                trackConversion({ source: "aktion", plz });
                trackAktion("form_submitted");
            } catch { /* nicht kritisch */ }
        } catch (err) {
            setStatus("error");
            setErrorMsg((err as Error).message || cfg.error_message || "Verbindung fehlgeschlagen.");
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid={`${testId}-success`}
                className="p-8 rounded-lg text-center"
                style={{ background: "#ffffff", border: "2px solid #D4A855" }}
            >
                <div
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full mx-auto"
                    style={{ background: "#F1D48A" }}
                >
                    <Check size={32} style={{ color: "#8A5A1D" }} strokeWidth={3} />
                </div>
                <h3 className="font-anton text-3xl md:text-4xl mt-5 uppercase" style={{ color: "#0F1830" }}>
                    Vielen Dank!
                </h3>
                <p className="mt-3 text-base" style={{ color: "#334155" }} data-testid={`${testId}-success-msg`}>
                    {cfg.success_message}
                </p>
            </div>
        );
    }

    const inputCls =
        "w-full px-4 py-3 rounded-md bg-white border-2 text-base focus:outline-none focus:ring-2 focus:ring-[#D4A855] transition";

    return (
        <form onSubmit={handleSubmit} className="grid gap-4" data-testid={`${testId}-form`} noValidate>
            {/* Honeypot */}
            <label
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
            >
                Website
                <input type="text" name="website" tabIndex={-1} autoComplete="off" />
            </label>

            <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: "#0F1830" }}>
                    {cfg.name_label || "Name"} *
                </label>
                <input
                    name="name" type="text" required
                    placeholder={cfg.name_placeholder || undefined}
                    className={inputCls}
                    style={{ borderColor: errs.name ? "#DC2626" : "#CBD5E1" }}
                    data-testid={`${testId}-name`}
                />
                {errs.name && <p className="mt-1 text-xs" style={{ color: "#DC2626" }}>{errs.name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: "#0F1830" }}>
                        {cfg.plz_label || "PLZ"} *
                    </label>
                    <input
                        name="plz" type="text" inputMode="numeric" pattern="[0-9]{5}" maxLength={5} required
                        placeholder={cfg.plz_placeholder || undefined}
                        className={inputCls}
                        style={{ borderColor: errs.plz ? "#DC2626" : "#CBD5E1" }}
                        data-testid={`${testId}-plz`}
                    />
                    {errs.plz && <p className="mt-1 text-xs" style={{ color: "#DC2626" }}>{errs.plz}</p>}
                </div>
                <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: "#0F1830" }}>
                        {cfg.phone_label || "Telefon"} *
                    </label>
                    <input
                        name="phone" type="tel" required
                        placeholder={cfg.phone_placeholder || undefined}
                        className={inputCls}
                        style={{ borderColor: errs.phone ? "#DC2626" : "#CBD5E1" }}
                        data-testid={`${testId}-phone`}
                    />
                    {errs.phone && <p className="mt-1 text-xs" style={{ color: "#DC2626" }}>{errs.phone}</p>}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: "#0F1830" }}>
                    {cfg.message_label || "Nachricht (optional)"}
                </label>
                <textarea
                    name="message" rows={3}
                    placeholder={cfg.message_placeholder || undefined}
                    className={inputCls}
                    style={{ borderColor: "#CBD5E1" }}
                    data-testid={`${testId}-message`}
                />
            </div>

            <label className="flex items-start gap-3 text-sm" style={{ color: "#0F1830" }}>
                <input
                    type="checkbox"
                    checked={datenschutz}
                    onChange={(e) => setDatenschutz(e.target.checked)}
                    required
                    className="mt-1 h-5 w-5"
                    data-testid={`${testId}-datenschutz`}
                />
                <span data-testid={`${testId}-privacy-text`}>
                    <PrivacyText
                        text={cfg.privacy_text || "Ich habe die {link} gelesen und stimme der Verarbeitung meiner Daten zu."}
                        linkText={cfg.privacy_link_text}
                        linkUrl={cfg.privacy_link_url}
                    /> *
                </span>
            </label>
            {errs.datenschutz && (
                <p className="text-xs" style={{ color: "#DC2626" }}>{errs.datenschutz}</p>
            )}

            {status === "error" && (
                <div
                    className="p-3 rounded text-sm"
                    style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" }}
                    data-testid={`${testId}-error`}
                >
                    {errorMsg}
                </div>
            )}

            {cfg.submit_target === "url" && cfg.submit_url ? (
                <a
                    href={cfg.submit_url}
                    target={/^https?:\/\//.test(cfg.submit_url) ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="hf-aktion-cta w-full justify-center inline-flex items-center gap-2"
                    style={styleToCss(cfg.submit_style)}
                    data-testid={`${testId}-submit`}
                >
                    {status === "sending" ? "Wird gesendet…" : cfg.submit_text}
                </a>
            ) : (
                <button
                    type="submit"
                    disabled={status === "sending"}
                    className="hf-aktion-cta w-full justify-center"
                    style={styleToCss(cfg.submit_style)}
                    data-testid={`${testId}-submit`}
                >
                    {status === "sending" ? "Wird gesendet…" : cfg.submit_text}
                </button>
            )}
        </form>
    );
}
